import React from 'react';
import {
  Show,
  SimpleShowLayout,
  TextField,
  DateField,
  ReferenceField,
  FunctionField,
  TopToolbar,
  Button,
  useRecordContext,
} from 'react-admin';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Typography, Divider } from '@mui/material';
import { downloadFileAuthenticated } from '../../utils/downloadFile';

const formTypeLabel = (record: { form?: { name?: string }; formName?: string | null }) =>
  record.form?.name || record.formName || '—';

const contextOrService = (record: {
  customerService?: { name?: string; customer?: { displayName?: string; company?: string } };
  article?: { title?: string } | null;
  equipment?: { name?: string } | null;
}) => {
  const cs = record.customerService;
  if (cs) return cs.name || cs.customer?.displayName || cs.customer?.company || '—';
  if (record.article?.title) return record.article.title;
  if (record.equipment?.name) return record.equipment.name;
  return '—';
};

const FormResponsesPanel = () => {
  const record = useRecordContext();
  if (!record?.formDefinition) return null;

  let fields: Array<{ label?: string; type?: string; userValue?: unknown }> = [];
  try {
    const raw =
      typeof record.formDefinition === 'string'
        ? JSON.parse(record.formDefinition as string)
        : record.formDefinition;
    fields = Array.isArray(raw?.fields) ? raw.fields : [];
  } catch {
    return <Typography color="text.secondary">Could not parse form responses.</Typography>;
  }

  if (fields.length === 0) return null;

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        Responses
      </Typography>
      <Divider sx={{ mb: 1 }} />
      <Box component="dl" sx={{ m: 0, display: 'grid', gap: 1 }}>
        {fields.map((f, i) => (
          <Box key={i} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '200px 1fr' }, gap: 1 }}>
            <Typography component="dt" variant="body2" fontWeight={600}>
              {f.label || f.type || 'Field'}
            </Typography>
            <Typography component="dd" variant="body2" sx={{ m: 0, whiteSpace: 'pre-wrap' }}>
              {f.userValue != null && String(f.userValue).trim() !== ''
                ? String(f.userValue)
                : '—'}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const CompletedFormsShowActions = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  return (
    <TopToolbar>
      <Button label="Back to submissions" onClick={() => navigate('/forms')} />
      <Button
        label="Download PDF"
        onClick={() => {
          if (id) {
            downloadFileAuthenticated(`/completed-forms/${id}/make-pdf`, `completed-form-${id}.pdf`);
          }
        }}
      />
    </TopToolbar>
  );
};

export const CompletedFormsShow = () => (
  <Show resource="completed-forms" title="Completed form" actions={<CompletedFormsShowActions />}>
    <SimpleShowLayout>
      <TextField source="id" />
      <DateField source="completedAt" label="Completed" showTime />
      <ReferenceField source="userId" reference="users" link="show">
        <FunctionField
          render={(r: { firstName?: string; lastName?: string; name?: string; email?: string }) =>
            [r.firstName, r.lastName].filter(Boolean).join(' ').trim() ||
            r.name?.trim() ||
            r.email ||
            '—'
          }
          label="Completed by"
        />
      </ReferenceField>
      <FunctionField label="Form type" render={(r: any) => formTypeLabel(r)} />
      <FunctionField label="Context" render={(r: any) => contextOrService(r)} />
      <ReferenceField source="formId" reference="forms" link={false} label="Form template">
        <TextField source="name" />
      </ReferenceField>
      <FormResponsesPanel />
    </SimpleShowLayout>
  </Show>
);
